import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useToast } from "../../app/toast";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Table,
  TableSkeleton,
  Td,
  Th,
} from "../../components/ui";
import { appsApi, queryKeys } from "../../lib/api/resources";
import type { RegisteredApp } from "../../lib/api/types";

/**
 * The apps allowed to request ads (ADS-0043).
 *
 * Before this screen, `appId` was free text on the campaign and chain forms. A
 * typo produced a campaign no app would ever request — and it failed silently,
 * because a campaign for a misspelled app looks exactly like one whose audience
 * has not shown up yet.
 *
 * Apps are **deactivated, never deleted**: their campaigns carry billing history
 * that has to stay explainable after the app is retired.
 */
export function AppListPage() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const apps = useQuery({
    queryKey: queryKeys.apps,
    queryFn: () => appsApi.list(),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ["apps"] });
  };

  const create = useMutation({
    mutationFn: appsApi.create,
    onSuccess: () => {
      toast.success("App registrada");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const update = useMutation({
    mutationFn: ({
      id,
      active,
      app,
    }: {
      id: string;
      active: boolean;
      app: RegisteredApp;
    }) =>
      appsApi.update(id, {
        displayName: app.displayName,
        active,
        notes: app.notes,
      }),
    onSuccess: () => {
      toast.success("App actualizada");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-headline-lg text-text-primary">Apps</h1>
        <p className="text-body-md text-text-secondary">
          Las apps que pueden pedir anuncios. Campañas y cadenas eligen de esta
          lista, para que un identificador mal escrito no cree trabajo que nadie
          va a pedir.
        </p>
      </header>

      <NewAppCard
        onCreate={(body) => create.mutate(body)}
        pending={create.isPending}
      />

      <Card className="p-0">
        {apps.isLoading ? (
          <div className="p-6">
            <TableSkeleton rows={3} columns={5} />
          </div>
        ) : apps.isError ? (
          <div className="p-6">
            <ErrorState
              message={(apps.error as Error).message}
              onRetry={() => void apps.refetch()}
            />
          </div>
        ) : apps.data!.length === 0 ? (
          <div className="p-6">
            <EmptyState
              title="Todavía no hay apps"
              description="Registra la primera para poder crear campañas y cadenas."
            />
          </div>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Nombre</Th>
                <Th>Identificador</Th>
                <Th>Campañas</Th>
                <Th>Cadenas</Th>
                <Th>Estado</Th>
                <Th> </Th>
              </tr>
            </thead>
            <tbody>
              {apps.data!.map((app) => (
                <tr key={app.id}>
                  <Td>{app.displayName}</Td>
                  <Td>
                    <span className="font-mono text-label-md">{app.appId}</span>
                  </Td>
                  <Td>{app.campaignCount}</Td>
                  <Td>{app.chainCount}</Td>
                  <Td>{app.active ? "Activa" : "Retirada"}</Td>
                  <Td>
                    <Button
                      variant="ghost"
                      onClick={() =>
                        update.mutate({ id: app.id, active: !app.active, app })
                      }
                    >
                      {app.active ? "Retirar" : "Reactivar"}
                    </Button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

function NewAppCard({
  onCreate,
  pending,
}: {
  onCreate: (body: { appId: string; displayName: string }) => void;
  pending: boolean;
}) {
  const [appId, setAppId] = useState("");
  const [displayName, setDisplayName] = useState("");

  const trimmedId = appId.trim();
  const trimmedName = displayName.trim();
  // Mirrors the server's rule, so a bad identifier is refused here rather than
  // coming back as a validation error from a round trip.
  const validId = /^[a-z0-9][a-z0-9._-]*$/.test(trimmedId);
  const canSubmit = validId && trimmedName.length > 0 && !pending;

  return (
    <Card>
      <div className="flex flex-wrap items-end gap-3">
        <Field
          label="Identificador"
          hint="Lo que la app envía como appId. Minúsculas, sin espacios."
          error={
            trimmedId.length > 0 && !validId
              ? "Solo minúsculas, dígitos, puntos, guiones y guiones bajos."
              : undefined
          }
        >
          <Input
            value={appId}
            placeholder="millonario-app"
            className="w-56 font-mono"
            onChange={(event) => setAppId(event.target.value)}
          />
        </Field>

        <Field label="Nombre">
          <Input
            value={displayName}
            placeholder="El Bloque Millonario"
            className="w-64"
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </Field>

        <div className="flex flex-col gap-1">
          <Button
            variant="primary"
            disabled={!canSubmit}
            onClick={() =>
              onCreate({ appId: trimmedId, displayName: trimmedName })
            }
          >
            Registrar app
          </Button>
          {!canSubmit && !pending && (
            <span className="text-label-md text-text-secondary">
              Indica identificador y nombre
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
